import axios from 'axios';
import toast from 'react-hot-toast';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api'),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const staffToken = localStorage.getItem('authToken');
    const memberToken = localStorage.getItem('memberToken');
    const token = staffToken || (memberToken && !memberToken.startsWith('{') ? memberToken : null);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized - clear auth and redirect
    if (error.response?.status === 401) {
      const isAuthVerify = error.config?.url?.includes('/auth/verify') || error.config?.url?.includes('/auth/member/me');
      const isLogin = error.config?.url?.includes('/auth/login') || error.config?.url?.includes('/auth/member-login');
      const staffRequest = Boolean(localStorage.getItem('authToken'));
      if (staffRequest) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      } else {
        localStorage.removeItem('memberToken');
        localStorage.removeItem('member');
      }
      if (!isAuthVerify && !isLogin && window.location.pathname !== '/' && window.location.pathname !== '/member') {
        window.location.href = staffRequest ? '/' : '/member';
      }
      return Promise.reject(error);
    }
    
    // Don't show error for network errors (server not reachable)
    // These are usually temporary and will be handled by the component
    if (!error.response) {
      // Network error - only log, don't show toast
      console.error('Network error:', error.message);
      return Promise.reject(error);
    }
    
    // Only show error toast for actual API errors (4xx, 5xx) that have a response
    // Skip showing errors for auth verification failures (expected behavior)
    const isAuthVerify = error.config?.url?.includes('/auth/verify');
    const isLogin = error.config?.url?.includes('/auth/login');
    if (!isAuthVerify && !isLogin) {
      const message = error.response?.data?.message || 'An error occurred';
      toast.error(message);
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },
  
  verify: async () => {
    const response = await api.get('/auth/verify');
    return response.data;
  },

  memberLogin: async (payload) => {
    const response = await api.post('/auth/member-login', payload);
    return response.data;
  },

  memberSignup: async (payload) => {
    const response = await api.post('/auth/member-signup', payload);
    return response.data;
  },

  memberPassword: async (payload) => {
    const response = await api.post('/auth/member-password', payload);
    return response.data;
  },

  memberMe: async () => {
    const response = await api.get('/auth/member/me');
    return response.data;
  },

  getUsers: async () => {
    const response = await api.get('/auth/users');
    return response.data;
  },

  createUser: async (payload) => {
    const response = await api.post('/auth/users', payload);
    return response.data;
  },

  deleteUser: async (id) => {
    const response = await api.delete(`/auth/users/${id}`);
    return response.data;
  },

  updateUserRole: async (id, role) => {
    const response = await api.put(`/auth/users/${id}/role`, { role });
    return response.data;
  }
};

/** Alias used by SettingsUsers */
export const usersAPI = {
  getAll: () => authAPI.getUsers(),
  create: (payload) => authAPI.createUser(payload),
  delete: (id) => authAPI.deleteUser(id),
  updateRole: (id, role) => authAPI.updateUserRole(id, role)
};

// Members API
export const membersAPI = {
  getAll: async () => {
    const response = await api.get('/members');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/members/${id}`);
    return response.data;
  },
  
  create: async (memberData) => {
    const response = await api.post('/members', memberData);
    return response.data;
  },
  
  update: async (id, memberData) => {
    const response = await api.put(`/members/${id}`, memberData);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/members/${id}`);
    return response.data;
  }
};

// Claims API
export const claimsAPI = {
  getAll: async () => {
    const response = await api.get('/claims');
    return response.data;
  },
  
  getByMember: async (memberId) => {
    const response = await api.get(`/claims/member/${memberId}`);
    return response.data;
  },
  
  create: async (claimData) => {
    const response = await api.post('/claims', claimData);
    return response.data;
  },
  
  update: async (id, claimData) => {
    const response = await api.put(`/claims/${id}`, claimData);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/claims/${id}`);
    return response.data;
  },
  
  getStats: async () => {
    const response = await api.get('/claims/stats/overview');
    return response.data;
  }
};

export default api;

